// Shorts.jsx
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router';

import { generateLangPath, getLangFromPath } from '../resource/langPath.js';
import Redirect from '../container/Redirect.js';
import ShortMessage from '../container/ShortMessage.js';
import {
  footerNavigationHeight,
  headerHeight,
  iPhoneXSeriesFooterPaddingInPWA,
} from '../style/variables.js';
import getCurrentUnixTimestamp from '../resource/getCurrentUnixTimestamp.js';
import { getIsIphoneXSeriesPWA } from '../resource/getUserAgent.js';
import { shorts as shortsDebug } from '../resource/debug.js';
import media from '../style/media.js';

const isServer = typeof window === 'undefined';

const log = shortsDebug.extend('log:component/Shorts');

const Shorts = ({
  deviceHeight = null,
  isClipFirstPageFetching = false,
  isClipFirstPageFetched = false,
  isConfigMerged = false,
  lastWatchedShortIndex = 0,
  clipFetchedFeedId = null,
  clipLatestFeedId = null,
  category = null,
  clipCategory = null,
  replace = () => null,
  fetchFeeds = () => null,
  itemIds = [],
  clipLatestTimestamp = null,
}) => {
  const { state, pathname } = useLocation();
  const currentLang = getLangFromPath(pathname);
  const [shortsDetailUrl, setShortsDetailUrl] = useState('');
  const listPath = state?.listPath;
  const isIphoneXSeriesPWA = getIsIphoneXSeriesPWA();

  log('Rendering', {
    isConfigMerged,
    listPath,
    category,
    lastWatchedShortIndex,
    ids: itemIds,
  });

  // Fetch clip feeds first page data when component mount
  useEffect(() => {
    const isFeedIdChanged = !!(
      clipLatestFeedId && clipLatestFeedId != clipFetchedFeedId
    );
    const canFetch = !isClipFirstPageFetched && !isClipFirstPageFetching;

    if (isConfigMerged && (isFeedIdChanged || canFetch) && !!clipCategory) {
      log('Fetch clip feeds first page data when component mount', {
        clipCategory,
      });
      fetchFeeds({
        page: 1,
        type: clipCategory,
        id: clipLatestFeedId,
        unixTimestamp: clipLatestTimestamp ? clipLatestTimestamp : undefined,
        shouldSkipLocalCache: !!clipLatestTimestamp,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch shorts category feeds first page data
  useEffect(() => {
    if (!isConfigMerged || !category) return;

    if (!itemIds.length) {
      log('Fetch shorts category feeds first page data', {
        category,
      });

      fetchFeeds({
        type: category,
        isAllowSameId: true,
        shouldReplaceIds: true,
        shouldIgnorePage: true,
        shouldSkipLocalCache: true,
        selectPath: ['shorts', category],
        unixTimestamp: getCurrentUnixTimestamp(), // force fetch new; make feed as random as possible
      });
    }
  }, [isConfigMerged, category, itemIds, fetchFeeds]);

  // Replace url /shorts to /shorts/:messageId.
  useEffect(() => {
    const id = itemIds[lastWatchedShortIndex || 0];
    const isAd = id?.startsWith('cta-');
    if (id) {
      const pathSegment = isAd ? `ad/${id.replace('cta-', '')}` : id;
      // No need to append `window.location.search`
      // since <Redirect /> would take care of it.
      const detailUrl = generateLangPath(`/shorts/${pathSegment}`, currentLang);
      log('Replace shorts url', {
        id,
        ids: itemIds,
        category,
      });
      setShortsDetailUrl(detailUrl);
    }
  }, [
    lastWatchedShortIndex,
    itemIds,
    listPath,
    category,
    replace,
    currentLang,
  ]);

  if (!isServer && !category && isConfigMerged) {
    return <Redirect to={generateLangPath('/', currentLang)} status={302} />;
  }

  if (!isServer && shortsDetailUrl) {
    return <Redirect to={shortsDetailUrl} status={302} />;
  }

  // Show skeleton in case of waiting too long
  return (
    <StyledShorts
      deviceHeight={deviceHeight}
      isIphoneXSeriesPWA={isIphoneXSeriesPWA}
    >
      <ShortMessage messageId="" isActive={false} deviceHeight={deviceHeight} />
    </StyledShorts>
  );
};

Shorts.propTypes = {
  clipFetchedFeedId: PropTypes.string,
  clipLatestFeedId: PropTypes.string,
  category: PropTypes.string,
  clipCategory: PropTypes.string,
  deviceHeight: PropTypes.number,
  isClipFirstPageFetching: PropTypes.bool,
  isClipFirstPageFetched: PropTypes.bool,
  isConfigMerged: PropTypes.bool,
  lastWatchedShortIndex: PropTypes.number,
  replace: PropTypes.func,
  fetchFeeds: PropTypes.func,
  itemIds: PropTypes.array,
  clipLatestTimestamp: PropTypes.number,
};

const StyledShorts = styled.div`
  width: 100%;
  overflow: hidden;
  text-align: center;
  height: ${({ deviceHeight }) =>
    deviceHeight ? `${deviceHeight}px` : '100vh'};
  padding-top: ${headerHeight}px;
  ${media.mobile`
    padding-top: 0;
    padding-bottom: 0;
    height: ${({ deviceHeight, isIphoneXSeriesPWA }) =>
      isIphoneXSeriesPWA
        ? `${
            deviceHeight -
            footerNavigationHeight -
            iPhoneXSeriesFooterPaddingInPWA
          }px`
        : `${deviceHeight - footerNavigationHeight}px`};
  `};
`;

export default Shorts;
